<template>
  <div>
    <v-card>
      <v-card-title class="text-h6 px-4 pt-4 text-left">
        Services Metadata Groups
      </v-card-title>
      <v-spacer></v-spacer>
      <div class="d-flex justify-end pa-4">
        <v-btn
          @click="openDialog()"
          color="success"
          rounded="0"
          elevation="2"
          title="New Group"
        >
          New Group
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        item-value="id"
        class="elevation-1"
        density="compact"
      >
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon :color="item.colour || 'grey'">{{
            "mdi-" + item.icon || "mdi-tag"
          }}</v-icon>
        </template>

        <template v-slot:[`item.country`]="{ item }">
          <v-chip v-if="item.eng" size="small" class="ma-1">England</v-chip>
          <v-chip v-if="item.wls" size="small" class="ma-1">Wales</v-chip>
          <v-chip v-if="item.sct" size="small" class="ma-1">Scotland</v-chip>
          <v-chip v-if="item.ni" size="small" class="ma-1">
            Northern Ireland
          </v-chip>
          <v-chip v-if="item.aus" size="small" class="ma-1">Australia</v-chip>
        </template>

        <template v-slot:[`item.public`]="{ item }">
          <v-icon :color="item.public ? 'success' : 'grey'">
            {{ item.public ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.custom`]="{ item }">
          <v-icon :color="item.custom ? 'success' : 'grey'">
            {{ item.custom ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.live`]="{ item }">
          <v-icon :color="item.live ? 'success' : 'grey'">
            {{ item.live ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.official_source`]="{ item }">
          <v-icon :color="item.official_source ? 'success' : 'grey'">
            {{ item.official_source ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            flat
            rounded="0"
            class="mr-1"
            @click="editItem(item)"
            title="Edit Group"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            flat
            rounded="0"
            class="mr-1"
            @click="duplicateItem(item)"
            title="Duplicate Group"
          >
            <v-icon color="primary">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn
            icon
            flat
            rounded="0"
            class="ml-1"
            @click="confirmDelete(item)"
            title="Delete Group"
          >
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="600">
      <v-card>
        <v-card-title
          :style="
            'background-color: ' +
            this.$store.state.config.siteConfig.toolbar_colour
          "
          class="text-white text-center"
        >
          Confirm Metadata Group Deletion?
        </v-card-title>
        <v-card-text class="text-center">
          Are you sure you want to delete the group "{{ itemToDelete?.name }}"?
          <div
            style="width: 100%"
            class="text-center mt-5 text-red font-weight-bold"
          >
            This action cannot be undone!!
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="grey"
            variant="outlined"
            rounded="0"
            @click="deleteDialog = false"
            class="rounded-0"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="outlined"
            rounded="0"
            @click="deleteItem(itemToDelete?.id)"
            :disabled="isDeleting"
            class="rounded-0"
          >
            <v-progress-circular
              v-if="isDeleting"
              indeterminate
              size="20"
              width="2"
              class="mr-2"
            ></v-progress-circular>
            {{ isDeleting ? "Deleting..." : "Delete" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card style="height: 80vh; display: flex; flex-direction: column">
        <v-card-title
          :style="{
            backgroundColor: $store.state.config.siteConfig.toolbar_colour,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }"
          class="text-white"
        >
          {{
            editedItem.id ? "Edit Metadata Group" : "Create New Metadata Group"
          }}
        </v-card-title>
        <v-card-text style="overflow-y: auto; flex: 1 1 auto; min-height: 0">
          <v-form ref="form" v-model="isFormValid">
            <v-text-field
              v-model="editedItem.name"
              label="Name"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[
                (v) => !!v || 'Name is required',
                (v) =>
                  v.length <= 100 || 'Name must be less than 100 characters',
              ]"
              :error-messages="fieldErrors.name"
              @input="fieldErrors = {}"
              required
            />
            <v-textarea
              v-model="editedItem.description"
              label="Description"
              variant="outlined"
              density="compact"
              rounded="0"
              auto-grow
              rows="3"
              :rules="[
                (v) => !!v || 'Description is required',
                (v) =>
                  v.length <= 5000 ||
                  'Description must be less than 5000 characters',
              ]"
              :error-messages="fieldErrors.description"
              @input="fieldErrors = {}"
              required
            />
            <IconSelection
              v-model="editedItem.icon"
              :icons="icons"
              button-text="Select Icon"
              button-variant="outlined"
              button-density="compact"
              button-rounded="0"
              :required="true"
              :error-messages="fieldErrors.icon ? [fieldErrors.icon] : []"
              @update:modelValue="fieldErrors = {}"
              class="mb-6"
            />
            <v-text-field
              v-model="editedItem.colour"
              label="Colour (e.g. red, blue)"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[
                (v) => !!v || 'Colour is required',
                (v) =>
                  /^#([0-9a-fA-F]{6})$/.test(v) ||
                  'Must be a valid 6-digit hex colour code (e.g. #FF5733)',
              ]"
              :error-messages="fieldErrors.colour"
              @input="
                editedItem.colour = editedItem.colour.toLowerCase();
                tempColor = editedItem.colour.toLowerCase();
                fieldErrors = {};
              "
              required
            >
              <template v-slot:append>
                <div
                  class="color-preview"
                  :style="{
                    backgroundColor: editedItem.colour || '#FFFFFF',
                    width: '24px',
                    height: '24px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }"
                  @click="showColorPicker = true"
                ></div>
              </template>
            </v-text-field>
            <v-dialog v-model="showColorPicker" max-width="300">
              <v-card>
                <v-card-text class="pa-0">
                  <v-color-picker
                    v-model="tempColor"
                    hide-inputs
                    mode="hex"
                  ></v-color-picker>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    color="error"
                    variant="text"
                    rounded="0"
                    @click="cancelColorPicker"
                  >
                    Cancel
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    variant="text"
                    rounded="0"
                    @click="closeColorPicker"
                  >
                    Select
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <div class="mb-4">
              <div class="text-subtitle-1 mb-3">Countries:</div>
              <v-row>
                <v-col cols="6">
                  <v-switch
                    v-model="editedItem.eng"
                    label="England"
                    density="compact"
                    color="success"
                  />
                </v-col>
                <v-col cols="6">
                  <v-switch
                    v-model="editedItem.wls"
                    label="Wales"
                    density="compact"
                    color="success"
                  />
                </v-col>
                <v-col cols="6">
                  <v-switch
                    v-model="editedItem.sct"
                    label="Scotland"
                    density="compact"
                    color="success"
                  />
                </v-col>
                <v-col cols="6">
                  <v-switch
                    v-model="editedItem.ni"
                    label="Northern Ireland"
                    density="compact"
                    color="success"
                  />
                </v-col>
                <v-col cols="6">
                  <v-switch
                    v-model="editedItem.aus"
                    label="Australia"
                    density="compact"
                    color="success"
                  />
                </v-col>
              </v-row>
            </div>
            <v-text-field
              v-model="editedItem.client_id"
              label="Client ID"
              type="number"
              variant="outlined"
              density="compact"
              rounded="0"
              readonly
              disabled
            />
            <v-select
              v-model="editedItem.client_type"
              label="Client Type"
              variant="outlined"
              density="compact"
              rounded="0"
              :items="[
                { title: 'Demo', value: 'demo' },
                { title: 'Ex', value: 'ex' },
                { title: 'Test', value: 'test' },
                { title: 'Live', value: 'live' },
              ]"
            />
            <v-switch
              v-model="editedItem.public"
              label="Public?"
              density="compact"
              color="success"
            />
            <v-switch
              v-model="editedItem.custom"
              label="Custom?"
              density="compact"
              color="success"
            />
            <v-switch
              v-model="editedItem.live"
              label="Live?"
              density="compact"
              color="success"
            />
            <v-switch
              v-model="editedItem.official_source"
              label="Official Source?"
              density="compact"
              color="success"
            />
          </v-form>
        </v-card-text>
        <v-card-actions
          style="position: sticky; bottom: 0; z-index: 2; background: white"
          class="pa-3"
        >
          <v-btn
            color="grey"
            variant="outlined"
            rounded="0"
            @click="closeDialog"
            class="rounded-0"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="outlined"
            rounded="0"
            @click="saveItem"
            :disabled="!isFormValid || isSaving"
            class="rounded-0"
          >
            <v-progress-circular
              v-if="isSaving"
              indeterminate
              size="20"
              width="2"
              class="mr-2"
            ></v-progress-circular>
            {{ isSaving ? "Saving..." : "Save" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IconSelection from "./IconSelection.vue";

export default {
  name: "ServicesMetadataGroups",
  components: {
    IconSelection,
  },
  data() {
    return {
      items: [],
      loading: false,
      dialog: false,
      deleteDialog: false,
      itemToDelete: null,
      editedItem: {},
      tempColor: "",
      showColorPicker: false,
      isFormValid: false,
      fieldErrors: {},
      isSaving: false,
      isDeleting: false,
      headers: [
        { title: "Icon", value: "icon", sortable: false, align: "center" },
        { title: "Name", value: "name", sortable: true, align: "center" },
        {
          title: "Description",
          value: "description",
          sortable: true,
          align: "center",
        },
        { title: "Country", value: "country", sortable: true, align: "center" },
        { title: "Public", value: "public", sortable: true, align: "center" },
        { title: "Custom", value: "custom", sortable: true, align: "center" },
        { title: "Live", value: "live", sortable: true, align: "center" },
        {
          title: "Official Source",
          value: "official_source",
          sortable: true,
          align: "center",
        },
        {
          title: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      icons: [],
    };
  },
  mounted() {
    this.fetchItems();
    this.fetchIcons();
  },
  methods: {
    emitMessage(payload) {
      this.emit.emit("systemMessage", payload);
    },
    fetchItems() {
      this.loading = true;
      this.$axios
        .get("/sd-services-groups")
        .then((res) => {
          this.items = res.data;
        })
        .catch((err) => {
          console.error(err);
          if (err.response?.status === 403) {
            this.emitMessage({
              title: "Access Denied",
              message: "Only OCSI users can access this feature.",
              colour: "error",
              timeout: 5000,
            });
          } else {
            this.emitMessage({
              title: "Error",
              message: "Failed to fetch metadata groups.",
              colour: "error",
              timeout: 5000,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchIcons() {
      this.$axios
        .get("/get-map-markers")
        .then((response) => {
          this.icons = response.data;
        })
        .catch((error) => {
          console.error(error);
          this.emit.emit("systemBusy", false);
        });
    },
    openDialog() {
      this.editedItem = {
        name: "",
        description: "",
        icon: "",
        colour: "",
        eng: false,
        wls: false,
        sct: false,
        ni: false,
        aus: false,
        public: false,
        custom: false,
        client_id: null,
        client_type: "",
        live: false,
        official_source: false,
      };
      this.tempColor = "";
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.editedItem = {};
      this.tempColor = "";
    },
    editItem(item) {
      this.editedItem = { ...item };
      this.tempColor = item.colour || "";
      this.dialog = true;
    },
    duplicateItem(item) {
      // Create a copy of the item with modifications
      this.editedItem = {
        ...item,
        id: null, // Remove ID so it creates a new record
        name: `${item.name} (copy)`,
        live: false,
      };
      this.tempColor = item.colour || "";
      this.dialog = true;
    },
    cancelColorPicker() {
      this.tempColor = this.editedItem.colour || "";
      this.showColorPicker = false;
    },
    closeColorPicker() {
      this.editedItem.colour = this.tempColor;
      this.showColorPicker = false;
    },
    saveItem() {
      // Validate form
      if (!this.$refs.form) {
        return;
      }

      this.$refs.form.validate().then(({ valid }) => {
        if (!valid) {
          this.emitMessage({
            title: "Validation Error",
            message: "Please fill in all required fields correctly.",
            colour: "error",
            timeout: 5000,
          });
          return;
        }

        // Check that at least one country is selected
        if (
          !this.editedItem.eng &&
          !this.editedItem.wls &&
          !this.editedItem.sct &&
          !this.editedItem.ni &&
          !this.editedItem.aus
        ) {
          this.emitMessage({
            title: "Validation Error",
            message: "Please select at least one country.",
            colour: "error",
            timeout: 5000,
          });
          return;
        }

        // Clear any previous field errors
        this.fieldErrors = {};

        // Ensure client_id is null if empty
        if (!this.editedItem.client_id) {
          this.editedItem.client_id = null;
        }

        // Show loading message
        this.emitMessage({
          title: this.editedItem.id ? "Updating" : "Creating",
          message: "Please wait...",
          colour: "warning",
          timeout: 3000,
        });

        this.isSaving = true;
        const req = this.editedItem.id
          ? this.$axios.put(
              `/sd-services-groups/${this.editedItem.id}`,
              this.editedItem,
            )
          : this.$axios.post(`/sd-services-groups`, this.editedItem);

        req
          .then(() => {
            this.emitMessage({
              title: this.editedItem.id ? "Updated" : "Created",
              message: `Group ${this.editedItem.name} successfully ${this.editedItem.id ? "updated" : "created"}.`,
              colour: "success",
              timeout: 3000,
            });
            this.dialog = false;
            this.editedItem = {};
            this.tempColor = "";
            this.fieldErrors = {};
            this.fetchItems();
          })
          .catch((err) => {
            console.error(err);
            let errorMessage = "Something went wrong saving the group.";

            if (err.response?.status === 403) {
              errorMessage = "Only OCSI users can access this feature.";
            } else if (err.response?.data?.errors) {
              this.fieldErrors = err.response.data.errors;
              // Get the first error message for the snackbar
              const firstError = Object.values(err.response.data.errors)[0];
              if (Array.isArray(firstError)) {
                errorMessage = firstError[0];
              }
            }

            this.emitMessage({
              title: err.response?.status === 403 ? "Access Denied" : "Error",
              message: errorMessage,
              colour: "error",
              timeout: 5000,
            });
          })
          .finally(() => {
            this.isSaving = false;
          });
      });
    },
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    deleteItem(id) {
      this.deleteDialog = false;
      this.isDeleting = true;
      this.emitMessage({
        title: "Deleting Group",
        message: "Please wait...",
        colour: "warning",
        timeout: 3000,
      });
      this.$axios
        .delete(`/sd-services-groups/${id}`)
        .then(() => {
          this.emitMessage({
            title: "Deleted",
            message: `Group ID ${id} deleted successfully.`,
            colour: "success",
            timeout: 3000,
          });
          this.fetchItems();
        })
        .catch((err) => {
          console.error(err);
          if (err.response?.status === 403) {
            this.emitMessage({
              title: "Access Denied",
              message: "Only OCSI users can access this feature.",
              colour: "error",
              timeout: 5000,
            });
          } else {
            this.emitMessage({
              title: "Error",
              message: "Failed to delete group.",
              colour: "error",
              timeout: 5000,
            });
          }
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
  },
};
</script>

<style scoped>
.v-data-table :deep(.v-data-table__tr) {
  transition: background-color 0.2s ease-in-out;
}

.v-data-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
  cursor: pointer;
}
</style>
